/**
 * usePrimeArtifactPropertyTable Hook
 * 
 * Manages property list fetching, filtering, search, and table data preparation
 * for Prime Artifact Properties Manager.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { message } from 'antd';
import { apiClient } from '../services/apiClient';
import { PropertyRelationshipService } from '../services/api/propertyRelationshipService';
import { RelationshipType } from '../types/propertyRelationships';

export interface PrimeArtifactProperty {
  id: string;
  prime_artifact_id: string;
  property_key: string;
  property_type: 'string' | 'number' | 'integer' | 'float' | 'boolean' | 'date' | 'date_range' | 'currency' | 'duration' | 'url' | 'uuid' | 'uuid[]' | 'list';
  description?: string;
  is_required: boolean;
  input_source?: 'user_input' | 'system_generated' | 'referenced_artifact' | 'transformed_reference';
  global_option_catalog_ref?: string;
  global_option_catalog_name?: string;
  default_value?: string;
  validation_rules?: Record<string, any>;
  property_scope?: 'artifact' | 'artifact_item';
  is_child_reference?: boolean;
  input_label?: string;
  input_type?: string;
  state_mappings?: Array<{
    id: string;
    state_name: string;
    mapping_type: 'created' | 'updated';
    transition_ref?: string;
    notes?: string;
  }>;
  referenced_artifacts?: Array<{
    id: string;
    referenced_artifact_type_id: string;
    referenced_artifact_type_name?: string;
    artifact_id?: string;
    reference_type?: 'uuid' | 'transformed';
    source_property_key?: string;
    transformation_logic?: any;
    relationship_type?: string;
    notes?: string;
  }>;
  created_at?: string;
  updated_at?: string;
}

export interface PrimeArtifactPropertyRow extends PrimeArtifactProperty {
  key: string;
}

interface UsePrimeArtifactPropertyTableProps {
  artifactId: string;
  onPropertiesUpdated?: () => void;
  onRowClick?: (record: PrimeArtifactPropertyRow) => void;
  selectedPropertyId?: string | null;
}

export const usePrimeArtifactPropertyTable = ({
  artifactId,
  onPropertiesUpdated,
  onRowClick,
  selectedPropertyId
}: UsePrimeArtifactPropertyTableProps) => {
  const [properties, setProperties] = useState<PrimeArtifactProperty[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterSource, setFilterSource] = useState<string | undefined>(undefined);
  const [filterType, setFilterType] = useState<string | undefined>(undefined);
  const [propertyGraphData, setPropertyGraphData] = useState<any>(null);
  const [selectedPropertyForDetails, setSelectedPropertyForDetails] = useState<string | null>(null);

  // Fetch properties
  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/prime-artifacts/${artifactId}/properties`);
      // Ensure response.data is an array
      const propertiesData = Array.isArray(response.data) ? response.data : (response.data?.properties || []);
      setProperties(propertiesData);
      
      // Load graph data for relationship information
      try {
        const graphData = await PropertyRelationshipService.getPropertyGraph(artifactId);
        setPropertyGraphData(graphData);
      } catch (error) {
        // Graph data is optional, don't fail if it's not available
        console.log('Graph data not available:', error);
      }
    } catch (error: any) {
      console.error('Error fetching properties:', error);
      void message.error('Failed to fetch properties');
    } finally {
      setLoading(false);
    }
  }, [artifactId]);

  // Get relationship type for a property
  const getPropertyRelationshipType = useCallback((property: PrimeArtifactProperty): RelationshipType => {
    return PropertyRelationshipService.getRelationshipType(property as any);
  }, []);

  // Get dependency counts for a property
  const getPropertyDependencyCounts = useCallback((propertyId: string) => {
    if (!propertyGraphData) return { dependencies: 0, dependents: 0 };
    const dependencies = propertyGraphData.edges.filter((e: any) => e.target === propertyId).length;
    const dependents = propertyGraphData.edges.filter((e: any) => e.source === propertyId).length;
    return { dependencies, dependents };
  }, [propertyGraphData]);

  // Filter properties based on search text and filters
  const filteredProperties = useMemo(() => {
    return (Array.isArray(properties) ? properties : []).filter(property => {
      // Search text filter - matches property key, description, or state names
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const matchesKey = property.property_key.toLowerCase().includes(searchLower);
        const matchesDescription = property.description?.toLowerCase().includes(searchLower) || false;
        const matchesStates = property.state_mappings?.some(m => 
          m.state_name.toLowerCase().includes(searchLower)
        ) || false;
        const matchesReferenced = property.referenced_artifacts?.some(ref => 
          ref.referenced_artifact_type_name?.toLowerCase().includes(searchLower)
        ) || false;
        
        if (!matchesKey && !matchesDescription && !matchesStates && !matchesReferenced) {
          return false;
        }
      }
      
      // Source filter
      if (filterSource && property.input_source !== filterSource) {
        return false;
      }
      
      // Type filter
      if (filterType && property.property_type !== filterType) {
        return false;
      }
      
      return true;
    });
  }, [properties, searchText, filterSource, filterType]);

  // Table data with row keys
  const tableData = useMemo((): PrimeArtifactPropertyRow[] => {
    return filteredProperties.map(property => ({
      ...property,
      key: property.id
    }));
  }, [filteredProperties]);

  // Load properties on mount and when artifactId changes
  useEffect(() => {
    void fetchProperties();
  }, [fetchProperties]);

  // Refresh handler
  const handleRefresh = useCallback(() => {
    void fetchProperties();
  }, [fetchProperties]);

  // Handle form success (refresh properties)
  const handleFormSuccess = useCallback(() => {
    void fetchProperties();
    onPropertiesUpdated?.();
  }, [fetchProperties, onPropertiesUpdated]);

  return {
    // State
    properties,
    loading,
    searchText,
    setSearchText,
    filterSource,
    setFilterSource,
    filterType,
    setFilterType,
    propertyGraphData,
    selectedPropertyForDetails,
    setSelectedPropertyForDetails,
    // Data
    tableData,
    filteredProperties,
    // Functions
    fetchProperties,
    handleRefresh,
    handleFormSuccess,
    getPropertyRelationshipType,
    getPropertyDependencyCounts
  };
};
